import React, { useEffect, useRef, useState, useMemo } from "react";
import { useSearchParams } from 'react-router-dom';
import { createPortal } from "react-dom";
import Layout from "../Layout/Layout";
import Pages from './LanguagePage';
import nodata from '../Layout/imgs/nodata0.png';
import './HowToStep.css';
import { elakayAutoText } from "../pageInside/dataElakay";
import { buildContentPageDataFromLanguageAPI } from "./contentPageUtils";
import { useContentPageOverride } from "./useContentPageOverride";
import { isAuthenticated } from "../auth";
import PageRenderer from "../pageInside/PageBuilder/PageRenderer";


let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
const __elakayText = (key, fallback) =>
  __elakayInfoSource?.laguangeElakay?.("frontElakayHardcodedText", __elakayInfoSource.languageAPI)?.["howToStep"]?.[key] || fallback;

/* ── helpers ─────────────────────────────────────────────────────── */

const stripHtml = (str) => String(str || '').replace(/<[^>]*>/g, '').trim();

const estimateReadTime = (sections) => {
    const wordCount = sections.reduce((acc, s) => {
        const texts = [s.title, ...(Array.isArray(s.text) ? s.text : [s.text])].join(' ');
        return acc + texts.split(/\s+/).filter(Boolean).length;
    }, 0);
    return Math.max(1, Math.ceil(wordCount / 200));
};

const buildTOC = (sections) =>
    sections
        .map((s, i) => {
            const title = stripHtml(s.title);
            return title.length > 2
                ? { id: `lp-section-${i}`, label: title.length > 55 ? title.slice(0, 52) + '…' : title, index: i }
                : null;
        })
        .filter(Boolean);

/* ── icons (inline SVG, no dep) ─────────────────────────────────── */

const IconClock = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
);

const IconList = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" />
        <line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" />
        <line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
);

/* ── component ───────────────────────────────────────────────────── */

const HowToStep = (props) => {
    const { infoSource } = props;
    __setElakayInfoSource(infoSource);
    const loading = infoSource.loading || infoSource.languageAPI?.loading;

    const [searchParams] = useSearchParams();
    const stepName = searchParams.get("step");
    const [activeTocId, setActiveTocId] = useState(null);
    const [tocMinimized, setTocMinimized] = useState(false);

    const divHowToStep = useRef(null);

    useEffect(() => {
        if (divHowToStep.current) {
            divHowToStep.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [stepName]);

    const languageAPI = infoSource.languageAPI || {};
    const menuLabels = infoSource?.laguangeElakay?.("menuAddLang", languageAPI) || {};
    // ARCHITECTURE CORRECTION: a Content Page's data source is fixed and
    // never redirectable -- `stepName` always drives both the data AND which
    // key resolves the override (a prior mission briefly let these diverge
    // via `dataSourceKey`; that was explicitly retracted). Only the DESIGN
    // (which HTML renders this content) can vary, and that is decided
    // entirely server-side (resolveContentPageTemplate's selected-Page-
    // Template-else-legacy-override logic) before this hook ever sees a
    // response -- see useContentPageOverride's own header comment.
    const contentPage = !loading
        ? buildContentPageDataFromLanguageAPI(languageAPI, stepName, { label: menuLabels[stepName] || stepName })
        : null;
    // Family "System Templates"/"Page Templates" override (task spec) --
    // resolved centrally via useContentPageOverride (see its own header
    // comment). Fails safe to null on any error, so the article rendering
    // below is completely unchanged whenever no override/template has
    // Priority ON for this stepName.
    const { doc: overridePageDoc, htmlSource: overrideHtmlSource, htmlSourcePages: overrideHtmlSourcePages } = useContentPageOverride(stepName);

    const stepSections = useMemo(
        () => (Array.isArray(contentPage?.sections) ? contentPage.sections : []),
        [contentPage]
    );
    const toc = useMemo(() => buildTOC(stepSections), [stepSections]);
    const readTime = useMemo(() => estimateReadTime(stepSections), [stepSections]);
    const articleTitle = menuLabels[stepName] || stepName || __elakayText("t002", "Guide");

    const handleTocClick = (id) => {
        setActiveTocId(id);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };


    /* ── article content ─────────────────────────────────────────── */

    const pageSteps = () => {
        // Priority ON for this step's system template -- render Family's
        // custom PageBuilder version instead of the hardcoded article
        // layout below. The route (?step=<stepName>) never changes; only
        // which version renders. See useContentPageOverride's own fail-safe
        // contract -- this stays null (never crashes) if anything's wrong.
        if (overridePageDoc) {
            return (
                <PageRenderer
                    doc={overridePageDoc}
                    htmlSource={overrideHtmlSource}
                    htmlSourcePages={overrideHtmlSourcePages}
                    pageData={contentPage}
                    infoSource={infoSource}
                    token={isAuthenticated()?.token}
                    device="desktop"
                />
            );
        }
        if (stepSections.length > 0) {
            return (
                <article className="lp-article">

                    {/* ── Article Header ── */}
                    <header className="lp-article-header">
                        <nav className="lp-breadcrumb" aria-label={elakayAutoText("h_6e5ce570b4af9c", "breadcrumb")}>
                            <span>{__elakayText("t004", "Elakay")}</span>
                            <span className="lp-bc-sep" aria-hidden="true">›</span>
                            <span className="lp-bc-current">{articleTitle}</span>
                        </nav>

                        <div className="lp-article-eyebrow">
                            <span className="lp-meta-tag">{__elakayText("t005", "Guide")}</span>
                        </div>

                        <h1 className="lp-article-title">{articleTitle}</h1>

                        <div className="lp-article-meta">
                            <span className="lp-meta-item">
                                <IconClock />
                                {readTime}&nbsp;{__elakayText("t006", "min read")}
                            </span>
                            {toc.length > 0 && (
                                <span className="lp-meta-item">
                                    <IconList />
                                    {toc.length}&nbsp;{__elakayText("t007", "sections")}
                                </span>
                            )}
                        </div>

                        <div className="lp-article-divider" aria-hidden="true" />
                    </header>

                    {/* ── Article body (TOC is a floating portal) ── */}
                    <div className="lp-article-layout no-toc">

                        {/* Article Body */}
                        <main
                            className="lp-article-body"
                            data-content-page-id={contentPage?._id || ""}
                            data-content-page-key={contentPage?.pageKey || stepName || ""}
                        >
                            <Pages
                                classAlert="lp-sections"
                                classPara="lp-section"
                                classText="lp-content-block"
                                classImg="lp-media-block"
                                paragra0={stepSections}
                                contentPage={contentPage}
                                infoSource={infoSource}
                            />
                        </main>
                    </div>
                </article>
            );
        }

        /* ── empty state ── */
        return (
            <div className="lp-empty-state">
                <img
                    src={nodata}
                    alt={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).howToStep.t001}
                />
                <p>{__elakayText("t003", "No steps found for this tutorial")}</p>
            </div>
        );
    };

    const tocPortal = toc.length > 1 && typeof document !== 'undefined'
        ? createPortal(
            <aside className="lp-toc-sidebar" aria-label={__elakayText("t008", "Table of contents")}>
                <div className="lp-toc-inner">
                    <div className="lp-toc-header-row">
                        <p className="lp-toc-heading">{__elakayText("t008", "Contents")}</p>
                        <button
                            type="button"
                            className="lp-toc-minimize-btn"
                            onClick={() => setTocMinimized(m => !m)}
                            title={tocMinimized ? elakayAutoText("h_9869e506c38f64", "Expand") : elakayAutoText("h_1c5b768c8e72a0", "Minimize")}
                        >{tocMinimized ? '▢' : '−'}</button>
                    </div>
                    {!tocMinimized && (
                        <ol className="lp-toc-list">
                            {toc.map((item) => (
                                <li key={item.id} className={`lp-toc-item${activeTocId === item.id ? ' active' : ''}`}>
                                    <button
                                        type="button"
                                        className="lp-toc-link"
                                        onClick={() => handleTocClick(item.id)}
                                        aria-label={item.label}
                                    >
                                        <span className="lp-toc-num">{item.index + 1}</span>
                                        <span className="lp-toc-label">{item.label}</span>
                                    </button>
                                </li>
                            ))}
                        </ol>
                    )}
                </div>
            </aside>,
            document.body
        )
        : null;

    return (
        <Layout
            title={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).howToStep.t002}
            description={elakayAutoText("h_41682905073222", "Node React E-commerce App")}
            className="container-fluid"
            infoSource={infoSource}
            SliderPunShow={false}
            setLoading={false}
            {...infoSource?.allSettings?.layout}
        >
            <div ref={divHowToStep} className="lp-page-wrapper" style={infoSource.styleNav}>
                {pageSteps()}
                {tocPortal}
            </div>
        </Layout>
    );
};

export default HowToStep;
